import { Duration } from "luxon";

import { createLogger } from "@/logger";
import { recordEvent } from "@/services/telemetry";

const log = createLogger("storage");

/**
 * How long a delete waits on other tabs before the caller stops treating it as pending.
 *
 * IndexedDB has no forced drop: `deleteDatabase` only completes once every other connection to the
 * name closes, and until it does, every later `open()` on that name queues behind it. Waiting
 * without a bound is what turns one stale tab into a dead spinner, so callers get an answer within
 * this window and decide what to show.
 */
export const BLOCKED_DELETE_TIMEOUT_MS = Duration.fromObject({ seconds: 5 }).toMillis();

/** Upper bound on an open that may be queued behind a pending delete on the same name. */
export const OPEN_TIMEOUT_MS = Duration.fromObject({ seconds: 10 }).toMillis();

/** Why a bounded delete stopped waiting. `blocked` means the delete is still queued in the browser. */
export type DeleteOutcome = "deleted" | "blocked" | "error";

/**
 * Makes `db` yield when another tab needs to upgrade or delete the database behind it.
 *
 * Without this the other tab's `deleteDatabase` blocks for as long as this connection lives, and
 * the pending delete stalls every subsequent open on the name — across tabs, not just in the tab
 * that asked. Registering it costs nothing when no one ever needs the database, so every opener
 * should call it rather than reasoning about whether its connection is short-lived.
 *
 * @param db - The freshly opened connection to register the handler on.
 * @param dbName - Database name, recorded with the telemetry event.
 * @returns The same connection, so callers can wrap an open in place.
 */
export function closeOnVersionChange(db: IDBDatabase, dbName: string): IDBDatabase {
  db.onversionchange = () => {
    db.close();
    log.info("[idb] Closed connection for another tab's version change", { dbName });
    recordEvent("store.idb.version_change_closed", { idb: { dbName } }, "diagnostic");
  };
  return db;
}

/**
 * Deletes a database, resolving within {@link BLOCKED_DELETE_TIMEOUT_MS} whatever the browser does.
 *
 * A blocked delete stays queued after this resolves — the browser completes it once the last
 * connection closes. The point of the bound is that no caller is left awaiting a promise that
 * cannot settle, and that "still blocked" reaches the caller as a value it can act on instead of
 * being swallowed by an `onblocked` handler that resolves as though the delete had succeeded.
 *
 * @param dbName - Database to delete.
 * @param timeoutMs - How long to wait before reporting `blocked`.
 * @returns What happened, for the caller to surface or count.
 */
export function deleteDatabaseBounded(dbName: string, timeoutMs = BLOCKED_DELETE_TIMEOUT_MS): Promise<DeleteOutcome> {
  return new Promise<DeleteOutcome>((resolve) => {
    let settled = false;
    const settle = (outcome: DeleteOutcome): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (outcome !== "deleted") {
        recordEvent("store.idb.delete_blocked", { idb: { dbName, outcome } }, "reliability");
      }
      resolve(outcome);
    };
    const timer = setTimeout(() => {
      log.warn("[idb] Database delete still blocked by another connection", { dbName });
      settle("blocked");
    }, timeoutMs);

    const request = indexedDB.deleteDatabase(dbName);
    request.onsuccess = () => settle("deleted");
    request.onerror = () => {
      log.warn("[idb] Database delete failed", { dbName, error: request.error });
      settle("error");
    };
    // Not a resolution: the delete is queued behind another connection and may still land inside
    // the window. Let the timer decide, so a sibling that closes in time still counts as deleted.
    request.onblocked = () => {
      log.warn("[idb] Database delete blocked, waiting", { dbName });
    };
  });
}

/**
 * Rejects an open that has not settled inside `timeoutMs`, which is how a caller escapes an open
 * queued behind a pending delete. A connection that arrives after the timeout is closed rather
 * than leaked.
 *
 * @param request - The in-flight open request to bound.
 * @param dbName - Database name, recorded with the telemetry event.
 * @param onUpgrade - Applied on `upgradeneeded`, before the connection is handed back.
 * @param timeoutMs - How long to wait before rejecting.
 * @throws When the open errors, is blocked, or exceeds `timeoutMs`.
 */
export function awaitOpen(
  request: IDBOpenDBRequest,
  dbName: string,
  onUpgrade?: (db: IDBDatabase) => void,
  timeoutMs = OPEN_TIMEOUT_MS,
): Promise<IDBDatabase> {
  return new Promise<IDBDatabase>((resolve, reject) => {
    let settled = false;
    const settle = (action: () => void): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      action();
    };
    const fail = (message: string, error?: unknown): void => {
      // A timed-out open can still error or unblock afterwards. Reporting before the settled check
      // would count one failure twice and inflate the rate this rollout is read against.
      if (settled) return;
      log.warn("[idb] Database open did not complete", { dbName, message, error });
      recordEvent("store.idb.open_blocked", { idb: { dbName, message } }, "reliability");
      settle(() => reject(error instanceof Error ? error : new Error(message)));
    };
    const timer = setTimeout(() => fail("IndexedDB open timeout"), timeoutMs);

    if (onUpgrade) request.onupgradeneeded = () => onUpgrade(request.result);
    request.onsuccess = () => {
      if (settled) {
        request.result.close();
        return;
      }
      settle(() => resolve(closeOnVersionChange(request.result, dbName)));
    };
    request.onerror = () => fail("IndexedDB open failed", request.error);
    request.onblocked = () => fail("IndexedDB open blocked");
  });
}
